import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: import.meta.env['VITE_API_URL'],
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
}

export const httpClient: AxiosInstance = axios.create(DEFAULT_CONFIG)

// Provider de token (Pull pattern)
type TokenProvider = () => string | null | Promise<string | null>;
type UnauthorizedHandler = () => void;

let getTokenFn: TokenProvider = () => null;
let onUnauthorized: UnauthorizedHandler = () => {};

/**
 * Configure la méthode de récupération du token.
 * À appeler au démarrage de l'application (Bootstrap).
 */
export const configureAuthGetter = (fn: TokenProvider) => {
  getTokenFn = fn;
};

/**
 * Configure le callback à appeler en cas d'erreur 401 (Unauthorized).
 * Exemple: Redirection vers le login ou refresh token manuel.
 */
export const configureUnauthorizedHandler = (fn: UnauthorizedHandler) => {
  onUnauthorized = fn;
};

httpClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getTokenFn();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interface explicite pour l'enveloppe technique Gateway
export interface ApiResponse<T = any> {
  data: T;
  statusCode: number;
  meta?: any;
}

// Type guard pour vérifier si la réponse est une enveloppe API valide
function isApiResponse(data: any): data is ApiResponse {
  return (
    data &&
    typeof data === 'object' &&
    'data' in data &&
    'statusCode' in data
  );
}

// Normalisation architecturale : déballage automatique de l'enveloppe { data: T }
httpClient.interceptors.response.use(
  (response) => {
    // Vérification stricte du format ApiResponse avant déballage
    if (isApiResponse(response.data)) {
      // On remplace le body par le contenu de 'data' pour que les clients ne voient que le payload
      response.data = response.data.data;
    }
    return response;
  },
  (error) => {
    // Gestion centralisée des erreurs 401
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      onUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Note: La classe statique HttpClient a été supprimée pour favoriser l'usage direct de l'instance 'httpClient'
// et permettre un meilleur tree-shaking ainsi qu'une testabilité accrue.

